import React, { useState } from 'react';

const inputStyle = {
  width: '100%', padding: '11px 14px', borderRadius: '8px',
  border: '1px solid var(--border)', backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)',
  fontSize: '14px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
};
const labelStyle = {
  fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)',
  textTransform: 'uppercase', letterSpacing: '0.07em', display: 'block', marginBottom: '6px',
};
const darkCard = { backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '14px' };

const INDUSTRIES = ['SaaS / Software', 'E-commerce', 'Fintech', 'Healthcare', 'EdTech', 'D2C / Consumer', 'Marketplace', 'Agency / Services', 'Manufacturing', 'Other'];

export default function CustomerInsights({ goBack }) {
  const [step, setStep] = useState('form');
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);
  const [openPlaybook, setOpenPlaybook] = useState(null);

  const [form, setForm] = useState({
    industry: '',
    businessModel: 'B2B',
    customerCount: '',
    mrr: '',
    avgOrderValue: '',
    churnRate: '',
    cac: '',
    ltv: '',
    topChallenge: '',
    additionalContext: '',
  });

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.industry || !form.topChallenge) {
      setError('Industry and top challenge are required.');
      return;
    }
    setStep('loading');
    try {
      const res = await fetch('/api/finance?action=customer-insights', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          industry:         form.industry,
          businessModel:    form.businessModel,
          customerCount:    form.customerCount ? Number(form.customerCount.replace(/,/g, '')) : null,
          mrr:              form.mrr ? Number(form.mrr.replace(/,/g, '')) : null,
          avgOrderValue:    form.avgOrderValue ? Number(form.avgOrderValue.replace(/,/g, '')) : null,
          churnRate:        form.churnRate ? Number(form.churnRate) : null,
          cac:              form.cac ? Number(form.cac.replace(/,/g, '')) : null,
          ltv:              form.ltv ? Number(form.ltv.replace(/,/g, '')) : null,
          topChallenge:     form.topChallenge,
          additionalContext: form.additionalContext,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Analysis failed');
      setReport(data);
      setStep('report');
    } catch (err) {
      setError(err.message);
      setStep('form');
    }
  };

  // ── FORM ──────────────────────────────────────────────────────────────────
  if (step === 'form') return (
    <section style={{ minHeight: '100vh', backgroundColor: 'var(--bg)', paddingTop: '80px' }}>
      <div style={{ maxWidth: '640px', margin: '0 auto', padding: '40px 24px' }}>
        <button onClick={goBack} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '13px', cursor: 'pointer', padding: 0, marginBottom: '32px', fontFamily: 'inherit' }}>← Back</button>
        <div style={{ marginBottom: '28px' }}>
          <div style={{ display: 'inline-block', backgroundColor: 'rgba(0,194,124,0.08)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', fontWeight: 700, color: 'var(--accent)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '14px' }}>Customer Insights</div>
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 10px', letterSpacing: '-0.02em' }}>Customer Intelligence</h1>
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.7, margin: 0 }}>Share your business metrics. We'll identify churn risks, surface growth levers, and give you a prioritised retention and acquisition playbook.</p>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Business profile */}
          <div style={{ ...darkCard, padding: '24px' }}>
            <p style={{ fontSize: '12px', fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 16px' }}>Business Profile</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Industry</label>
                <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.industry} onChange={set('industry')}>
                  <option value="">Select industry…</option>
                  {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Business Model</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {['B2B', 'B2C', 'B2B2C'].map(m => (
                    <button key={m} type="button" onClick={() => setForm(f => ({ ...f, businessModel: m }))}
                      style={{ flex: 1, padding: '9px 0', borderRadius: '7px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', backgroundColor: form.businessModel === m ? 'var(--bg-secondary)' : 'transparent', border: `1px solid ${form.businessModel === m ? 'var(--accent)' : 'var(--border)'}`, color: form.businessModel === m ? 'var(--text-primary)' : 'var(--text-tertiary)', transition: 'all 0.15s' }}
                    >{m}</button>
                  ))}
                </div>
              </div>
              <div>
                <label style={labelStyle}>Number of Customers</label>
                <input style={inputStyle} placeholder="e.g. 500" value={form.customerCount} onChange={set('customerCount')} />
              </div>
            </div>
          </div>

          {/* Key metrics */}
          <div style={{ ...darkCard, padding: '24px' }}>
            <p style={{ fontSize: '12px', fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 16px' }}>Key Metrics</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              <div>
                <label style={labelStyle}>MRR / Monthly Revenue (Rs.)</label>
                <input style={inputStyle} placeholder="e.g. 500000" value={form.mrr} onChange={set('mrr')} />
              </div>
              <div>
                <label style={labelStyle}>Avg Order / Contract Value (Rs.)</label>
                <input style={inputStyle} placeholder="e.g. 12000" value={form.avgOrderValue} onChange={set('avgOrderValue')} />
              </div>
              <div>
                <label style={labelStyle}>Monthly Churn Rate (%)</label>
                <input style={inputStyle} type="number" min="0" max="100" step="0.1" placeholder="e.g. 3.5" value={form.churnRate} onChange={set('churnRate')} />
              </div>
              <div>
                <label style={labelStyle}>CAC — Customer Acquisition Cost (Rs.)</label>
                <input style={inputStyle} placeholder="e.g. 2500" value={form.cac} onChange={set('cac')} />
              </div>
              <div>
                <label style={labelStyle}>LTV — Lifetime Value (Rs.)</label>
                <input style={inputStyle} placeholder="e.g. 36000" value={form.ltv} onChange={set('ltv')} />
              </div>
            </div>
          </div>

          {/* Challenge */}
          <div>
            <label style={labelStyle}>What is your biggest customer challenge right now?</label>
            <textarea style={{ ...inputStyle, height: '80px', resize: 'vertical' }} placeholder="e.g. Churn spiked to 8% after we raised prices. Struggling to retain SMB customers beyond 3 months." value={form.topChallenge} onChange={set('topChallenge')} />
          </div>
          <div>
            <label style={labelStyle}>Anything else relevant?</label>
            <textarea style={{ ...inputStyle, height: '70px', resize: 'vertical' }} placeholder="e.g. We just launched a new pricing tier, sales cycle is 45 days, most churn happens at month 3" value={form.additionalContext} onChange={set('additionalContext')} />
          </div>

          {error && <p style={{ fontSize: '13px', color: '#EF4444', margin: 0 }}>{error}</p>}

          <button type="submit" style={{ width: '100%', padding: '13px', borderRadius: '9px', background: 'var(--accent)', color: '#fff', border: 'none', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            Generate Customer Insights →
          </button>
        </form>
      </div>
    </section>
  );

  // ── LOADING ───────────────────────────────────────────────────────────────
  if (step === 'loading') return (
    <section style={{ minHeight: '100vh', backgroundColor: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: '48px', height: '48px', border: '3px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.9s linear infinite', margin: '0 auto 20px' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Analysing your customer data…</p>
      </div>
    </section>
  );

  // ── REPORT ────────────────────────────────────────────────────────────────
  const { healthScore, churnRiskFactors, growthLevers, retentionPlaybook, acquisitionInsights, topAction } = report;

  const scoreColor = healthScore >= 70 ? '#10B981' : healthScore >= 40 ? '#F59E0B' : '#EF4444';
  const scoreLabel = healthScore >= 70 ? 'Healthy' : healthScore >= 40 ? 'At Risk' : 'Critical';

  return (
    <section style={{ minHeight: '100vh', backgroundColor: 'var(--bg)', paddingTop: '80px' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 24px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <div style={{ display: 'inline-block', backgroundColor: 'rgba(0,194,124,0.08)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', fontWeight: 700, color: 'var(--accent)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px' }}>Customer Intelligence Report</div>
            <h1 style={{ fontSize: '24px', fontWeight: 700, color: 'var(--text-primary)', margin: 0, letterSpacing: '-0.02em' }}>{form.industry} · {form.businessModel}</h1>
          </div>
          <button onClick={() => setStep('form')} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-secondary)', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}>Recalculate</button>
        </div>

        {/* Health score + top action */}
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '12px', marginBottom: '24px', alignItems: 'stretch' }}>
          {healthScore != null && (
            <div style={{ ...darkCard, padding: '20px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: '120px' }}>
              <p style={{ fontSize: '42px', fontWeight: 800, color: scoreColor, margin: 0, letterSpacing: '-0.03em' }}>{healthScore}</p>
              <p style={{ fontSize: '11px', fontWeight: 700, color: scoreColor, textTransform: 'uppercase', letterSpacing: '0.08em', margin: '4px 0 0' }}>{scoreLabel}</p>
              <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', margin: '2px 0 0' }}>Customer Health</p>
            </div>
          )}
          {topAction && (
            <div style={{ backgroundColor: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: '14px', padding: '18px 20px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <span style={{ fontSize: '16px', flexShrink: 0 }}>⚡</span>
              <div>
                <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 6px' }}>Top Priority Action</p>
                <p style={{ fontSize: '14px', color: 'var(--text-primary)', lineHeight: 1.7, margin: 0 }}>{topAction}</p>
              </div>
            </div>
          )}
        </div>

        {/* Churn Risk Factors */}
        {churnRiskFactors?.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 12px' }}>Churn Risk Factors</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {churnRiskFactors.map((r, i) => {
                const riskColor = r.severity === 'high' ? '#EF4444' : r.severity === 'medium' ? '#F59E0B' : '#10B981';
                return (
                  <div key={i} style={{ ...darkCard, padding: '14px 18px', borderLeft: `3px solid ${riskColor}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: riskColor, textTransform: 'uppercase', letterSpacing: '0.07em', border: `1px solid ${riskColor}40`, borderRadius: '4px', padding: '2px 7px' }}>{r.severity}</span>
                      <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>{r.factor}</p>
                    </div>
                    <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '0 0 6px', lineHeight: 1.6 }}>{r.detail}</p>
                    {r.fix && <p style={{ fontSize: '12px', color: riskColor, margin: 0, fontWeight: 500 }}>Fix: {r.fix}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Growth Levers */}
        {growthLevers?.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 12px' }}>Growth Levers</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' }}>
              {growthLevers.map((g, i) => (
                <div key={i} style={{ ...darkCard, padding: '16px 18px', borderTop: '2px solid var(--accent)' }}>
                  <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 6px' }}>{g.lever}</p>
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '0 0 8px', lineHeight: 1.6 }}>{g.detail}</p>
                  {g.potentialImpact && <p style={{ fontSize: '12px', color: '#10B981', margin: 0, fontWeight: 600 }}>↑ {g.potentialImpact}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Retention Playbook */}
        {retentionPlaybook?.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 12px' }}>Retention Playbook</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {retentionPlaybook.map((p, i) => (
                <div key={i} style={{ ...darkCard, overflow: 'hidden' }}>
                  <button
                    type="button"
                    onClick={() => setOpenPlaybook(openPlaybook === i ? null : i)}
                    style={{ width: '100%', background: 'none', border: 'none', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--accent)', minWidth: '20px' }}>{i + 1}</span>
                      <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>{p.tactic}</span>
                    </div>
                    <span style={{ color: 'var(--text-tertiary)', fontSize: '16px', flexShrink: 0 }}>{openPlaybook === i ? '−' : '+'}</span>
                  </button>
                  {openPlaybook === i && (
                    <div style={{ padding: '0 20px 18px' }}>
                      <p style={{ fontSize: '13px', color: 'var(--text-primary)', lineHeight: 1.7, margin: '0 0 10px' }}>{p.detail}</p>
                      {p.implementation && (
                        <div style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '7px', padding: '12px 14px' }}>
                          <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.07em', margin: '0 0 6px' }}>How to implement</p>
                          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.65 }}>{p.implementation}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Acquisition Insights */}
        {acquisitionInsights && (
          <div style={{ ...darkCard, padding: '20px 24px', marginBottom: '40px', borderLeft: '3px solid #10B981' }}>
            <p style={{ fontSize: '11px', fontWeight: 700, color: '#10B981', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>Acquisition Insights</p>
            <p style={{ fontSize: '14px', color: 'var(--text-primary)', lineHeight: 1.75, margin: 0 }}>{acquisitionInsights}</p>
          </div>
        )}

        <button onClick={goBack} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-secondary)', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>← Back to Dashboard</button>
      </div>
    </section>
  );
}
